import { useRef } from 'react';
import type { FC } from 'react';

import type { FormikProps } from 'formik';
import { Form } from 'formik';

import SubmitButton from 'src/app/components/Elements/Button/Submit';
import GenericError from 'src/app/components/Error/Generic';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import { useFocusOnFirstFormError } from 'src/app/components/Form/Hooks/useFocusOnFirstFormError';
import TextInput from 'src/app/components/Form/TextInput';
import { LeadFormResponse } from 'src/app/components/Pages/Doctors/LeadForm/ResponseMessages/LeadFormResponse';
import type { WhitePaperDownloadResponse } from 'src/app/components/Pages/Doctors/LeadForm/ResponseMessages/WhitePaperDownloadResponse';

export interface FormValues {
  doctorName: string;
  email: string;
  zipCode: string;
  specialty: string;
}

interface LeadFormProps extends FormikProps<FormValues> {
  name: string;
  className: string;
  inputStyle: string;
  buttonContainerStyle: string;
  submitButtonLabel?: string;
  isWhite: boolean;
  hasLightRedError: boolean;
  formName: string;

  /**
   * ResponseMessage
   *
   * @example <LeadForm ResponseMessage={WhitePaperDownloadResponse}/>
   */
  ResponseMessage?: typeof LeadFormResponse | typeof WhitePaperDownloadResponse;

  /**
   * Optional success state function. If specified, this will get called
   * when Formik.status.success === true.
   * The submitButtonLabel is an optional prop. If unspecfied, the default submit
   * button label is 'Learn More.'
   */
  onSuccess?(): void;
}

/**
 * @TODO Kevin Wang
 *
 * When the redesigned doctors page is deployed, we are free to delete old logic
 * - isDeliverable
 * - status.isDeliverable
 */
const Renderer: FC<LeadFormProps> = ({
  isValid,
  isSubmitting,
  status,
  className,
  inputStyle,
  buttonContainerStyle,
  onSuccess,
  ResponseMessage = LeadFormResponse,
  submitButtonLabel,
  formName,
  errors,
  isValidating,
  isWhite,
  hasLightRedError,
}) => {
  const formRef = useRef(null);

  useFocusOnFirstFormError(formRef, errors, isSubmitting, isValidating, false, formName);

  if (status?.success) {
    // status.isDeliverable is updated when the form is submitted
    onSuccess?.();
    return <ResponseMessage isDeliverable={status?.isDeliverable} />;
  } else {
    return (
      <Form ref={formRef}>
        <GenericError />
        <SegmentFormIncomplete />
        <div className={className}>
          <TextInput
            className={inputStyle}
            name="doctorName"
            type="text"
            label="Your Name"
            id={`${formName}-doctorName`}
            isWhite={isWhite}
            hasLightRedError={hasLightRedError}
          />
          <TextInput
            className={inputStyle}
            name="email"
            type="email"
            label="Your Email"
            id={`${formName}-email`}
            isWhite={isWhite}
            hasLightRedError={hasLightRedError}
          />
          <TextInput
            className={inputStyle}
            name="specialty"
            type="text"
            label="Specialty"
            id={`${formName}-specialty`}
            isWhite={isWhite}
            hasLightRedError={hasLightRedError}
          />
          <TextInput
            className={inputStyle}
            name="zipCode"
            type="text"
            label="Office Zip Code"
            maxLength="5"
            id={`${formName}-zipCode`}
            isWhite={isWhite}
            hasLightRedError={hasLightRedError}
          />
          <div className={buttonContainerStyle}>
            <SubmitButton
              isSubmitting={isSubmitting}
              type="submit"
              fixedWidth
              size="medium"
              style="primary"
              disabled={isSubmitting || !isValid}
            >
              {!isSubmitting && (submitButtonLabel ? submitButtonLabel : `Learn More`)}
            </SubmitButton>
          </div>
        </div>
      </Form>
    );
  }
};

export default Renderer;
